/**
 * Page Error Boundary
 *
 * Wraps individual pages to catch and display errors gracefully.
 * Provides automatic error recovery and navigation options.
 */

import React, { Component, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorFallback } from './ErrorFallback';
import { reportError } from '@/lib/logging';

interface PageErrorBoundaryProps {
  children: ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
}

interface PageErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class PageErrorBoundaryClass extends Component<
  PageErrorBoundaryProps & { navigate: ReturnType<typeof useNavigate> },
  PageErrorBoundaryState
> {
  constructor(props: PageErrorBoundaryProps & { navigate: ReturnType<typeof useNavigate> }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): PageErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    reportError('Page error boundary caught error', error, {
      componentStack: errorInfo.componentStack,
    });
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  goBack = () => {
    this.props.navigate(-1);
  };

  render() {
    if (this.state.hasError && this.state.error) {
      const FallbackComponent = this.props.fallback;

      if (FallbackComponent) {
        return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          resetError={this.resetError}
          onGoBack={this.goBack}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Wrapper component that provides navigation context
 */
export function PageErrorBoundary(props: PageErrorBoundaryProps) {
  const navigate = useNavigate();
  return <PageErrorBoundaryClass {...props} navigate={navigate} />;
}
